import { Button, Col, Form, Input, Row, Table, Typography, Space } from "antd";
import { EditOutlined } from "@ant-design/icons";
import React, { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import ViewBtn from "../Buttons/ViewBtn";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";
import { addDepartment, getDepartments } from "./departmentApis";

function CustomTable({ list, onEdit }) {
  const [columnsToShow, setColumnsToShow] = useState([]);

  const columns = [
    {
      id: 1,
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
    },
    {
      id: 2,
      title: "Name",
      dataIndex: "DepartmentName",
      key: "DepartmentName",
    },
    {
      id: 3,
      title: "Action",
      dataIndex: "id",
      key: "action",
      render: (id, record) => (
        <Space>
          {/* <ViewBtn path={`/admin/department/${id}/`} /> */}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    setColumnsToShow(columns);
  }, []);

  const columnsToShowHandler = (val) => {
    setColumnsToShow(val);
  };

  const addKeys = (arr) =>
    arr.map((i, index) => ({ ...i, key: i.id, serial: index + 1 }));

  const flattenData = (data) => {
    return data.map((item) => ({
      id: item.id,
      company_id: item.company_id,
      name: item.DepartmentName,
      status: item.status,
      user: Array.isArray(item.user)
        ? item.user
            .map((u) => `${u.firstName || ""} ${u.lastName || ""}`.trim())
            .join(", ")
        : "No Users",
    }));
  };

  return (
    <>
      <div className="text-center my-2 flex justify-between">
        <h5 className="department-list-title text-color-2 text-xl mb-2">
          Department List
        </h5>
        {list && (
          <div>
            <CsvLinkBtn>
              <CSVLink
                data={flattenData(list)}
                className="btn btn-dark btn-sm mb-1"
                filename="departments"
              >
                Download CSV
              </CSVLink>
            </CsvLinkBtn>
          </div>
        )}
      </div>

      {list && (
        <div style={{ marginBottom: "30px" }}>
          <ColVisibilityDropdown
            options={columns}
            columns={columns}
            columnsToShowHandler={columnsToShowHandler}
          />
        </div>
      )}

      <Table
        scroll={{ x: true, y: "100%" }}
        loading={!list}
        columns={columnsToShow}
        dataSource={list ? addKeys(list) : []}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />
    </>
  );
}

const AddDepartment = ({ drawer }) => {
  const [list, setList] = useState(null);
  const [loader, setLoader] = useState(false);
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentDepartmentId, setCurrentDepartmentId] = useState(null); // Initialize as null to catch issues

  useEffect(() => {
    getDepartments()
      .then((d) => {
        // console.log("Fetched departments:", d); // Log the fetched departments
        setList(d);
      })
      // .catch((error) => // console.log("Error fetching departments:", error));
  }, []);

  const handleEdit = (record) => {
    // console.log("Editing record:", record); // Log the record being edited
    setIsEditMode(true);
    const departmentId = record.id || record.DepartmentId; // Check for both id and DepartmentId
    // console.log("Setting currentDepartmentId to:", departmentId); // Log the ID being set
    setCurrentDepartmentId(departmentId);
    form.setFieldsValue({
      name: record.DepartmentName,
    });
  };

  const resetForm = () => {
    form.resetFields();
    setIsEditMode(false);
    setCurrentDepartmentId(null);
  };

  const onFinish = async (values) => {
    setLoader(true);
    try {
      const departmentIdToUse = isEditMode && currentDepartmentId ? currentDepartmentId : 0;
      const departmentData = {
        DepartmentId: departmentIdToUse, // Ensure DepartmentId is always defined
        DepartmentName: values.name,
        DataStatus: 1,
      };

      // console.log("Sending departmentData:", departmentData); // Log the payload

      const resp = await addDepartment(departmentData);

      if (resp.message === "success") {
        const updatedList = await getDepartments();
        // console.log("Updated departments:", updatedList); // Log the updated list
        setList(updatedList);
        resetForm();
        toast.success(
          isEditMode
            ? "Department updated successfully!"
            : "Department added successfully!"
        );
      } else {
        toast.error(
          isEditMode ? "Failed to update department" : "Failed to add department"
        );
      }
    } catch (error) {
      // console.error("Error in onFinish:", error);
      toast.error(
        isEditMode ? "Failed to update department" : "Failed to add department"
      );
    } finally {
      setLoader(false);
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    const errorMessages = errorFields
      .map((field) => field.errors.join(", "))
      .join("; ");
    toast.warning(`Please fix the following errors: ${errorMessages}`);
    setLoader(false);
  };

  const { Title } = Typography;

  return (
    <Fragment>
      <Row className="add-department-row" gutter={[16, 16]} justify="space-between">
        <Col
          xs={24}
          sm={24}
          md={10}
          lg={10}
          xl={8}
          className="add-department-form-col column-design border rounded card-custom"
        >
          <Title level={4} className="m-2 mt-5 mb-5 text-center">
            {isEditMode ? "Edit Department" : "Add Department"}
          </Title>
          <Form
            form={form}
            style={{ marginBottom: "40px" }}
            name="department-form"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 16 }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            preserve={false}
          >
            <Form.Item
              label={<span className="required-label">Name</span>}
              name="name"
              rules={[
                { required: true, message: "Please input department name!" },
              ]}
            >
              <Input placeholder="e.g., Test" />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
              <Space>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  block
                  loading={loader}
                >
                  {isEditMode ? "Update Department" : "Add New Department"}
                </Button>
                {isEditMode && (
                  <Button size="large" onClick={resetForm}>
                    Cancel
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Col>

        <Col xs={24} sm={24} md={14} lg={14} xl={16} className="add-department-table-col">
          <div className="ant-card p-4 rounded department-table-container">
          <CustomTable list={list} onEdit={handleEdit} />
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default AddDepartment;